import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
} from '@mui/material';
import { ExternalLink, Check } from 'lucide-react';
import {
  computeSeedingEmbedding,
  DEFAULT_VOTE_WEIGHTS,
} from '@musedrops/shared/src/seeding';
import type { SeedingVote } from '@musedrops/shared/src/seeding';
import { useNotify } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../services/supabase';
import { PageToolbar } from '../../../shared/components/PageToolbar';

// --- Types ---

interface SeedingStoryRow {
  id: string;
  story_slug: string;
  rank: number;
  cluster_size: number;
  pool_count: number;
  algo: string;
  min_pairwise_cosine_similarity: number | null;
  centroid_embedding: string | null;
  created_at: string;
}

interface StoryDetail {
  id: string;
  slug: string;
  embedding: string | null;
  shows: { slug: string } | null;
  showName: string | null;
  storyTitle: string | null;
  storySubtitle: string | null;
}

interface SimilarStory {
  slug: string;
  similarity: number;
  title?: string | null;
  subtitle?: string | null;
  showName?: string | null;
  clusterId?: string | null;
  clusterDescription?: string | null;
}

type VoteValue = 'yes' | 'maybe' | 'no';

// --- Helpers ---

// parseEmbedding is duplicated here because the canonical version lives in
// supabase/functions/_shared/rec-pipeline.ts (Deno runtime), which can't be
// imported from the admin (Vite/Node). The logic is trivial: JSON.parse + guard.
function parseEmbedding(raw: string | null): number[] | null {
  if (!raw) return null;
  try {
    const arr = JSON.parse(raw);
    return Array.isArray(arr) && arr.length > 0 ? arr : null;
  } catch {
    return null;
  }
}

/** Fetch similar stories via pgvector RPC (server-side cosine similarity + per-show cap) */
async function fetchSimilarViaRPC(
  embedding: number[],
  maxPerShow?: number,
  limit = 100,
  excludeSlugs: string[] = [],
): Promise<SimilarStory[]> {
  const embStr = `[${embedding.join(',')}]`;
  const params: Record<string, unknown> = {
    query_embedding: embStr,
    target_lang: 'en',
    pool_size: limit,
    exclude_slugs: excludeSlugs,
  };
  if (maxPerShow !== undefined) params.max_per_show = maxPerShow;
  const { data, error } = await supabase.rpc('match_stories_for_recommendations', params);

  if (error) throw error;

  return (data || []).map((row: any) => ({
    slug: row.slug,
    similarity: Math.round(row.similarity * 1000) / 1000,
    clusterId: row.cluster_id ?? null,
  }));
}

/** Enrich similar stories with translation data and cluster descriptions */
async function enrichWithTranslations(stories: SimilarStory[]): Promise<SimilarStory[]> {
  if (stories.length === 0) return stories;
  const slugs = stories.map(s => s.slug);
  const clusterIds = [...new Set(stories.map(s => s.clusterId).filter(Boolean))] as string[];

  const transPromise = supabase
    .from('stories_admin_enriched')
    .select('slug, title, subtitle, show_name')
    .in('slug', slugs)
    .eq('lang', 'en');

  const clusterPromise = clusterIds.length > 0
    ? supabase.from('clusters').select('id, description').in('id', clusterIds)
    : Promise.resolve({ data: [] as { id: string; description: string | null }[] });

  const [{ data: transData }, { data: clusterData }] = await Promise.all([
    transPromise,
    clusterPromise,
  ]);

  const transMap = new Map((transData || []).map(d => [d.slug, d]));
  const clusterMap = new Map((clusterData || []).map(c => [c.id, c.description]));

  return stories.map(s => {
    const t = transMap.get(s.slug);
    return {
      ...s,
      title: t?.title,
      subtitle: t?.subtitle,
      showName: t?.show_name,
      clusterDescription: s.clusterId ? clusterMap.get(s.clusterId) ?? null : null,
    };
  });
}

const ClusterSummary = ({ stories }: { stories: { clusterId?: string | null }[] }) => {
  const counts = new Map<string, number>();
  for (const s of stories) {
    if (s.clusterId) counts.set(s.clusterId, (counts.get(s.clusterId) ?? 0) + 1);
  }
  if (counts.size === 0) return null;
  const total = stories.length;
  const sorted = [...counts.entries()].sort((a, b) => b[1] - a[1]);
  return (
    <Typography variant="caption" sx={{ mt: 0.5, display: 'block', fontFamily: 'monospace', color: 'text.secondary' }}>
      Clusters: {sorted.map(([id, count]) => `${id}:${Math.round(count / total * 100)}%`).join(' | ')}
    </Typography>
  );
};

/** Shared story cell: thumbnail + show name / title / subtitle */
const StoryCell = ({
  slug,
  title,
  subtitle,
  showName,
  onTitleClick,
}: {
  slug: string;
  title?: string | null;
  subtitle?: string | null;
  showName?: string | null;
  onTitleClick?: () => void;
}) => {
  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <img
        src={`${apiBaseUrl}/drops/${slug}/image?size=70`}
        alt=""
        width={70}
        height={70}
        style={{ borderRadius: 4, objectFit: 'cover', flexShrink: 0 }}
      />
      <Box sx={{ minWidth: 0 }}>
        {showName && (
          <Typography variant="caption" color="text.secondary" display="block" noWrap>
            {showName}
          </Typography>
        )}
        {onTitleClick ? (
          <Button
            variant="text"
            size="small"
            onClick={onTitleClick}
            sx={{ textTransform: 'none', p: 0, minWidth: 0, textAlign: 'left', justifyContent: 'flex-start', lineHeight: 1.3 }}
          >
            {title || slug}
          </Button>
        ) : (
          <Typography variant="body2" noWrap>{title || slug}</Typography>
        )}
        {subtitle && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.3 }} noWrap>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

// --- Component ---

export const SeedingList = () => {
  const [rows, setRows] = useState<SeedingStoryRow[]>([]);
  const [storyDetails, setStoryDetails] = useState<Map<string, StoryDetail>>(new Map());
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);

  // Cluster count (k) for regeneration
  const [clusterCount, setClusterCount] = useState(8);

  // Vote simulation state
  const [votes, setVotes] = useState<Map<string, VoteValue>>(new Map());

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTitle, setDialogTitle] = useState('');
  const [similarStories, setSimilarStories] = useState<SimilarStory[]>([]);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [dialogMode, setDialogMode] = useState<'cluster' | 'simulation'>('cluster');
  const [activeClusterRowId, setActiveClusterRowId] = useState<string | null>(null);
  const [selectingSlug, setSelectingSlug] = useState<string | null>(null);

  const notify = useNotify();
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      // Fetch seeding stories
      const { data: seeding, error: onbErr } = await supabase
        .from('seeding_stories')
        .select('id, story_slug, rank, cluster_size, pool_count, algo, min_pairwise_cosine_similarity, centroid_embedding, created_at')
        .order('rank');

      if (onbErr) throw onbErr;
      const onbRows = (seeding || []) as SeedingStoryRow[];
      setRows(onbRows);

      if (onbRows.length === 0) {
        setStoryDetails(new Map());
        return;
      }

      // Fetch story details from enriched view (already joins translations + show)
      const slugs = onbRows.map(r => r.story_slug);
      const { data: stories, error: stErr } = await supabase
        .from('stories_admin_enriched')
        .select('id, slug, title, subtitle, show_slug, show_name')
        .in('slug', slugs)
        .eq('lang', 'en');

      if (stErr) throw stErr;

      // Also fetch embeddings from the stories table (not in the view)
      const { data: embeddings, error: embErr } = await supabase
        .from('stories')
        .select('slug, embedding')
        .in('slug', slugs);

      if (embErr) throw embErr;
      const embMap = new Map((embeddings || []).map(e => [e.slug, e.embedding]));

      const detailMap = new Map<string, StoryDetail>();
      for (const s of (stories || [])) {
        detailMap.set(s.slug, {
          id: s.id,
          slug: s.slug,
          embedding: embMap.get(s.slug) || null,
          shows: s.show_slug ? { slug: s.show_slug } : null,
          showName: s.show_name || null,
          storyTitle: s.title || null,
          storySubtitle: s.subtitle || null,
        });
      }
      setStoryDetails(detailMap);
    } catch (err: any) {
      notify(`Failed to load seeding stories: ${err.message}`, { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke('refresh-seeding-stories', {
        body: { maxN: clusterCount },
      });
      if (error) throw error;
      if (!data.success) throw new Error(data.error);
      notify(
        `Regenerated: ${data.result.nSelected} stories from pool of ${data.result.poolCount}`,
        { type: 'success' },
      );
      setVotes(new Map()); // reset votes on regenerate
      await fetchData();
    } catch (err: any) {
      notify(`Regeneration failed: ${err.message}`, { type: 'error' });
    } finally {
      setRegenerating(false);
    }
  };

  const handleClusterClick = async (row: SeedingStoryRow) => {
    const centroidEmb = parseEmbedding(row.centroid_embedding);
    if (!centroidEmb) {
      notify('No centroid embedding stored — regenerate to compute centroids', { type: 'warning' });
      return;
    }

    setDialogTitle(`Cluster ${row.rank}: stories similar to centroid`);
    setDialogMode('cluster');
    setActiveClusterRowId(row.id);
    setDialogOpen(true);
    setDialogLoading(true);
    setSimilarStories([]);

    try {
      const ranked = await fetchSimilarViaRPC(centroidEmb, 40);
      setSimilarStories(await enrichWithTranslations(ranked));
    } catch (err: any) {
      notify(`Failed to load similar stories: ${err.message}`, { type: 'error' });
    } finally {
      setDialogLoading(false);
    }
  };

  const handleVoteChange = (slug: string, value: VoteValue | null) => {
    setVotes(prev => {
      const next = new Map(prev);
      if (value === null) {
        next.delete(slug);
      } else {
        next.set(slug, value);
      }
      return next;
    });
  };

  // Mirror submit-seeding-votes (#763): a vote contributes the seat's region
  // anchor (centroid_embedding); the story's own embedding is only a fallback.
  const buildSimulationVotes = (): SeedingVote[] => {
    const seedingVotes: SeedingVote[] = [];
    for (const [slug, vote] of votes) {
      const detail = storyDetails.get(slug);
      if (!detail) continue;
      const row = rows.find(r => r.story_slug === slug);
      const embedding =
        parseEmbedding(row?.centroid_embedding ?? null) ?? parseEmbedding(detail.embedding);
      if (!embedding) continue;
      seedingVotes.push({ storySlug: slug, vote, embedding });
    }
    return seedingVotes;
  };

  const handleSimulateProfile = async () => {
    const seedingVotes = buildSimulationVotes();

    if (seedingVotes.length === 0) {
      notify('Select at least one vote to simulate', { type: 'warning' });
      return;
    }

    const result = computeSeedingEmbedding(seedingVotes, DEFAULT_VOTE_WEIGHTS);

    if (!result.embedding) {
      notify('All votes are "no" — no profile embedding produced', { type: 'info' });
      return;
    }

    const { yes, maybe, no } = result.counts;
    setDialogTitle(
      `Simulated profile (${yes} yes, ${maybe} maybe, ${no} no — confidence ${result.confidence.toFixed(2)})`,
    );
    setDialogMode('simulation');
    setActiveClusterRowId(null);
    setDialogOpen(true);
    setDialogLoading(true);
    setSimilarStories([]);

    try {
      const ranked = await fetchSimilarViaRPC(result.embedding, 40);
      setSimilarStories(await enrichWithTranslations(ranked));
    } catch (err: any) {
      notify(`Failed to load similar stories: ${err.message}`, { type: 'error' });
    } finally {
      setDialogLoading(false);
    }
  };

  const handleSimulateSession = async () => {
    // Same vote vectors as profile simulation; results are session-like.
    const seedingVotes = buildSimulationVotes();

    if (seedingVotes.length === 0) {
      notify('Select at least one vote to simulate', { type: 'warning' });
      return;
    }

    const result = computeSeedingEmbedding(seedingVotes, DEFAULT_VOTE_WEIGHTS);

    if (!result.embedding) {
      notify('All votes are "no" — no profile embedding produced', { type: 'info' });
      return;
    }

    const { yes, maybe, no } = result.counts;
    setDialogTitle(
      `Simulated session (${yes} yes, ${maybe} maybe, ${no} no — 1 per show, top 10)`,
    );
    setDialogMode('simulation');
    setActiveClusterRowId(null);
    setDialogOpen(true);
    setDialogLoading(true);
    setSimilarStories([]);

    try {
      const seedingSlugs = rows.map(r => r.story_slug);
      const ranked = await fetchSimilarViaRPC(result.embedding, 10, 1, seedingSlugs);
      setSimilarStories(await enrichWithTranslations(ranked));
    } catch (err: any) {
      notify(`Failed to simulate session: ${err.message}`, { type: 'error' });
    } finally {
      setDialogLoading(false);
    }
  };

  const handleSelectStory = async (slug: string) => {
    if (!activeClusterRowId) return;
    setSelectingSlug(slug);
    try {
      const { error } = await supabase
        .from('seeding_stories')
        .update({ story_slug: slug })
        .eq('id', activeClusterRowId);
      if (error) throw error;
      notify(`Selected "${slug}" as cluster representative`, { type: 'success' });
      setDialogOpen(false);
      await fetchData();
    } catch (err: any) {
      notify(`Failed to select story: ${err.message}`, { type: 'error' });
    } finally {
      setSelectingSlug(null);
    }
  };

  // Meta info from first row
  const meta = rows.length > 0 ? rows[0] : null;
  const hasVotes = votes.size > 0;

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <PageToolbar left={<Typography variant="h6">Seeding Stories</Typography>} />
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress size={24} />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <PageToolbar
        left={<Typography variant="h6">Seeding Stories</Typography>}
        right={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <FormControl size="small" sx={{ minWidth: 70 }}>
              <InputLabel id="k-label">Count</InputLabel>
              <Select
                labelId="k-label"
                label="Count"
                value={clusterCount}
                onChange={(e) => setClusterCount(Number(e.target.value))}
                sx={{ '& .MuiSelect-select': { py: 0.5 } }}
              >
                {Array.from({ length: 19 }, (_, i) => i + 2).map(n => (
                  <MenuItem key={n} value={n}>{n}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              size="small"
              onClick={handleRegenerate}
              disabled={regenerating}
            >
              {regenerating ? 'Generating...' : 'Generate'}
            </Button>
          </Box>
        }
      />

      {meta && rows.length > 0 && (() => {
        const maxClusterSize = Math.max(...rows.map(r => r.cluster_size));
        const maxClusterPct = meta.pool_count > 0 ? maxClusterSize / meta.pool_count : 0;
        const pctLabel = `Max cluster: ${(maxClusterPct * 100).toFixed(1)}%`;
        const pctColor: 'success' | 'warning' | 'error' =
          maxClusterPct <= 0.20 ? 'success' : maxClusterPct <= 0.30 ? 'warning' : 'error';

        return (
          <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
            <Chip label={`Pool: ${meta.pool_count} stories`} size="small" variant="outlined" />
            <Chip label={`Algorithm: ${meta.algo}`} size="small" variant="outlined" />
            <Tooltip
              title="Largest cluster as % of pool. If a user votes No on this story, this % of the corpus gets no preference signal. Target: under 20% (green). Above 30% suggests increasing k or diversifying content."
              arrow
            >
              <Chip label={pctLabel} size="small" color={pctColor} />
            </Tooltip>
            {meta.min_pairwise_cosine_similarity != null && (
              <Tooltip
                title="Lowest cosine similarity between any two selected stories. Lower = more diverse coverage. Values below 0.5 indicate good spread across the embedding space."
                arrow
              >
                <Chip
                  label={`Min pairwise similarity: ${meta.min_pairwise_cosine_similarity.toFixed(3)}`}
                  size="small"
                  variant="outlined"
                />
              </Tooltip>
            )}
            <Chip
              label={`Generated: ${new Date(meta.created_at).toLocaleString()}`}
              size="small"
              variant="outlined"
            />
          </Box>
        );
      })()}

      {rows.length === 0 ? (
        <Card sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="textSecondary">
            No seeding stories yet. Click Regenerate to create the initial set.
          </Typography>
        </Card>
      ) : (
        <>
          <Card>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={50}>Rank</TableCell>
                    <TableCell width={170}>Vote</TableCell>
                    <TableCell>Story</TableCell>
                    <TableCell width={120} align="right">Cluster Size</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map(row => {
                    const detail = storyDetails.get(row.story_slug);
                    const vote = votes.get(row.story_slug) ?? null;
                    return (
                      <TableRow key={row.id} hover>
                        <TableCell>{row.rank}</TableCell>
                        <TableCell>
                          <ToggleButtonGroup
                            value={vote}
                            exclusive
                            size="small"
                            onChange={(_, v) => handleVoteChange(row.story_slug, v as VoteValue | null)}
                            sx={{
                              '& .MuiToggleButton-root': {
                                py: 0.25,
                                px: 1,
                                fontSize: '0.75rem',
                                textTransform: 'none',
                              },
                              '& .Mui-selected.vote-no': {
                                bgcolor: 'error.light',
                                color: 'error.contrastText',
                                '&:hover': { bgcolor: 'error.main' },
                              },
                              '& .Mui-selected.vote-maybe': {
                                bgcolor: 'warning.light',
                                color: 'warning.contrastText',
                                '&:hover': { bgcolor: 'warning.main' },
                              },
                              '& .Mui-selected.vote-yes': {
                                bgcolor: 'success.light',
                                color: 'success.contrastText',
                                '&:hover': { bgcolor: 'success.main' },
                              },
                            }}
                          >
                            <ToggleButton value="no" className="vote-no">No</ToggleButton>
                            <ToggleButton value="maybe" className="vote-maybe">Maybe</ToggleButton>
                            <ToggleButton value="yes" className="vote-yes">Yes</ToggleButton>
                          </ToggleButtonGroup>
                        </TableCell>
                        <TableCell>
                          <StoryCell
                            slug={row.story_slug}
                            title={detail?.storyTitle}
                            subtitle={detail?.storySubtitle}
                            showName={detail?.showName}
                            onTitleClick={detail ? () => navigate(`/stories/${detail.id}`) : undefined}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            variant="text"
                            size="small"
                            onClick={() => handleClusterClick(row)}
                            sx={{ textTransform: 'none', p: 0, minWidth: 0 }}
                          >
                            {row.cluster_size}
                            {meta && meta.pool_count > 0 && (
                              <Typography
                                component="span"
                                variant="caption"
                                sx={{ ml: 0.5, color: 'text.secondary' }}
                              >
                                ({(row.cluster_size / meta.pool_count * 100).toFixed(0)}%)
                              </Typography>
                            )}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>

          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleSimulateProfile}
              disabled={!hasVotes}
            >
              Simulate Profile
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleSimulateSession}
              disabled={!hasVotes}
            >
              Simulate Session
            </Button>
          </Box>
        </>
      )}

      {/* Similar Stories Dialog (shared by cluster click and profile simulation) */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent dividers>
          {dialogLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : similarStories.length === 0 ? (
            <Typography color="textSecondary" sx={{ p: 2 }}>
              No similar stories found.
            </Typography>
          ) : (
            <Box>
            <TableContainer sx={{ maxHeight: 500 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell width={40}>#</TableCell>
                    <TableCell>Story</TableCell>
                    <TableCell>Cluster</TableCell>
                    <TableCell width={100} align="right">Similarity</TableCell>
                    <TableCell width={80} align="center">&nbsp;</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {similarStories.map((s, i) => (
                    <TableRow key={s.slug} hover>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell>
                        <StoryCell
                          slug={s.slug}
                          title={s.title}
                          subtitle={s.subtitle}
                          showName={s.showName}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', verticalAlign: 'top' }}>
                        {s.clusterId && (
                          <Box>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                              {s.clusterId}
                            </Typography>
                            {s.clusterDescription && (
                              <Typography variant="caption" display="block" color="text.secondary" sx={{ lineHeight: 1.3 }}>
                                {s.clusterDescription}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell align="right">{s.similarity.toFixed(3)}</TableCell>
                      <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title="Open on musedrops.com" arrow>
                          <IconButton
                            size="small"
                            href={`https://musedrops.com/drops/${s.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            component="a"
                          >
                            <ExternalLink size={14} />
                          </IconButton>
                        </Tooltip>
                        {dialogMode === 'cluster' && (
                          <Tooltip title="Use this story as cluster representative" arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleSelectStory(s.slug)}
                              disabled={selectingSlug !== null}
                              color="primary"
                            >
                              {selectingSlug === s.slug ? (
                                <CircularProgress size={14} />
                              ) : (
                                <Check size={14} />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <ClusterSummary stories={similarStories} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
